import { PortalIdEnum } from '../../models/portal-id-enum';
import { Injectable } from '@angular/core';
import { PortalSettings } from 'src/app/view-models/portal-management/portal-settings';
import { Observable, from } from 'rxjs';
import { PortalSettingsResponse } from 'src/app/view-models/portal-management/portal-settings-response';
import { ListResponse } from 'src/app/view-models/list-response';
import { PortalServiceHttp } from 'src/app/http-services/portal-http-service';
import { TeamService } from '../../../core/services/team.service';
import { AuthenticationService } from 'src/app/modules/core/services/authentication.service';
import { PortalBootstrapService } from 'src/app/modules/core/services/portal-bootstrap.service';
import { Auth0ClientService } from 'src/app/modules/core/services/auth0-client.service';
import { ITeamTemplate } from 'src/app/modules/core/team-configuration/ITeamTemplate';
import { TeamTemplateComponent } from 'src/app/modules/core/generic/team-template.component';

@Injectable({ providedIn: 'root' })
export class PortalService extends TeamTemplateComponent {
	public portalSettings: PortalSettings = {} as PortalSettings;
	private paymentGateway: string;
	public template: ITeamTemplate;
	private portalCode: number;
	private portalSettingsText: string;
	private settingsLoadPromise: Promise<PortalSettings>;

	constructor(
		private portalServiceHttp: PortalServiceHttp,
		templateService: TeamService,
		private authenticationService: AuthenticationService,
		private portalBootstrap: PortalBootstrapService,
		private auth0Client: Auth0ClientService,
	) {
		super(templateService);
		this.portalCode = templateService.teamTemplate.portalId;
		this.portalSettingsText = localStorage.getItem(`${this.portalCode}-portal-settings`);
		this.template = templateService.teamTemplate;

		this.loadPortalSettingsIfLoggedIn();
	}

	// Loads portal settings once we know the user is authenticated. For Auth0-enabled portals the
	// session lives in the Auth0 SDK (no `token-<team>` cookie is ever set), so the cookie-based
	// isUserLoggedIn() check reports "logged out" and the settings would never load. Mirror the
	// token interceptor: branch on auth0Enabled and use the Auth0 SDK's async check there.
	private loadPortalSettingsIfLoggedIn() {
		if (this.portalBootstrap.auth0Enabled) {
			from(this.auth0Client.isAuthenticated()).subscribe(isAuthenticated => {
				if (isAuthenticated) {
					this.initPortalSettings(this.portalCode);
				}
			});
		} else if (this.isUserLoggedIn()) {
			this.initPortalSettings(this.portalCode);
		}
	}

	private isUserLoggedIn() {
		return this.authenticationService.isLoggedInPortal(this.template.teamId) ? true : false;
	}

	private initPortalSettings(portalCode: number) {
		this.portalServiceHttp.GetPortalSettings().subscribe(portalSettingsResult => {
			if (portalSettingsResult.isSuccess) {
				this.applyPortalSettings(portalSettingsResult, portalCode);
			}
		});
	}

	// Ensures portal settings (incl. CUSTOMER_PAYMENT_GATEWAY -> paymentGateway) are loaded and returns
	// a promise that resolves with them. Callers that must NOT fall back to the hard-coded template
	// default acquiring system (e.g. checkout) await this. Short-circuits once loaded and de-dupes
	// concurrent loads via a cached promise.
	public ensurePortalSettingsLoaded(): Promise<PortalSettings> {
		if (this.portalSettings && this.portalSettings.paymentGateway) {
			return Promise.resolve(this.portalSettings);
		}
		if (this.settingsLoadPromise) {
			return this.settingsLoadPromise;
		}
		this.settingsLoadPromise = this.portalServiceHttp.GetPortalSettings().toPromise()
			.then(portalSettingsResult => {
				if (portalSettingsResult && portalSettingsResult.isSuccess) {
					this.applyPortalSettings(portalSettingsResult, this.portalCode);
				}
				this.settingsLoadPromise = null;
				return this.portalSettings;
			})
			.catch(error => {
				this.settingsLoadPromise = null;
				throw error;
			});
		return this.settingsLoadPromise;
	}

	private applyPortalSettings(portalSettingsResult: any, portalCode: number) {
		if (!this.portalSettings) {
			this.portalSettings = {} as PortalSettings;
		}
		const ShowSeasonTicketsPage = portalSettingsResult.data.results.find(ps => {
			return ps.code === 'HideSeasonTicketsPage';
		});
				const showCancellationPolicyResult = portalSettingsResult.data.results.find(ps => {
					return ps.code === 'HideConflictResolutionPolicy';
				});
				const showConflictResPolicyResult = portalSettingsResult.data.results.find(ps => {
					return ps.code === 'HideConflictResolutionPolicy';
				});
				const showFooterGuide = portalSettingsResult.data.results.find(ps => {
					return ps.code === 'ShowFooterGuide';
				});
				const showRenewSeasonTicketResult = portalSettingsResult.data.results.find(ps => {
					return ps.code === 'ShowRenewSeasonTicket';
				});
				const showMarketingTooltip = portalSettingsResult.data.results.find(ps => {
					return ps.code === 'ShowMarketingTooltip';
				});
				const paymentGateway = portalSettingsResult.data.results.find(ps => {
					return ps.code === 'CUSTOMER_PAYMENT_GATEWAY';
				});
				const ShowMembershipPopup = portalSettingsResult.data.results.find(ps => {
					return ps.code === 'ShowMembershipPopup';
				});
				const isPrintableByCustomer = portalSettingsResult.data.results.find(ps => {
					return ps.code === 'isPrintableByCustomer';
				});

				this.portalSettings.canShowSeasonTicketsPage = ShowSeasonTicketsPage ? ShowSeasonTicketsPage.value == 'true' : true;
				this.portalSettings.showCancellationPolicy = showCancellationPolicyResult ? showCancellationPolicyResult.value == 'false' : false;
				this.portalSettings.showConflictResPolicy = showConflictResPolicyResult ? showConflictResPolicyResult.value == 'false' : false;
				this.portalSettings.showRenewSeasonTicket = showRenewSeasonTicketResult ? showRenewSeasonTicketResult.value == 'true' : true;
				this.portalSettings.showMarketingTooltip = showMarketingTooltip ? showMarketingTooltip.value == 'true' : false;
				this.portalSettings.showMembershipPopup = ShowMembershipPopup ? ShowMembershipPopup.value == 'true' : false;
				this.portalSettings.isPrintableByCustomer = isPrintableByCustomer ? isPrintableByCustomer.value == 'true' : false;
				this.portalSettings.paymentGateway = paymentGateway.value;

				localStorage.setItem(`${portalCode}-portal-settings`, JSON.stringify(this.portalSettings));

				this.templateService.setAcquiringSystem(this.portalSettings.paymentGateway);
	}

	resetPortalSettings() {
		this.portalSettings = undefined;
	}

	getPortalSettings() {
		this.loadPortalSettingsIfLoggedIn();
		return this.portalSettings;
	}

	public haveMembershipFee(): boolean {
		return this.portalSettings.showMembershipPopup;
	}
	
	public canShowLoyaltyPage(portalId: number): boolean {
		switch (portalId) {
			case PortalIdEnum.OtherTeam:
				return false;
			case PortalIdEnum.Omonoia:
				return true;
			case PortalIdEnum.Apoel:
				return true;
			case PortalIdEnum.Olympiakos:
				return false;
			case PortalIdEnum.Aek:
				return false;
			case PortalIdEnum.Paphos:
				return false;
			case PortalIdEnum.Paeek:
				return false;
			case PortalIdEnum.Doxa:
				return false;
			case PortalIdEnum.Kop:
				return false;
			default:
				return false;
		}
	}

	public canShowInstalmentsOption(portalId: number): boolean {
		switch (portalId) {
			case PortalIdEnum.OtherTeam:
				return false;
			case PortalIdEnum.Omonoia:
					return false;
			default:
					return false;
		}
	}

	public canShowLegalNoticeOnFooter(portalId: number): boolean {
		switch (portalId) {
			case PortalIdEnum.OtherTeam:
				return false;
			case PortalIdEnum.Apoel:
				return true;
			default:
				return false;
		}
	}

	public canShowPricingRulesPage(portalId: number): boolean {
		switch (portalId) {
			case PortalIdEnum.OtherTeam:
				return false;
			case PortalIdEnum.Omonoia:
				return false;
			case PortalIdEnum.Apoel:
				return false;
			case PortalIdEnum.Olympiakos:
				return false;
			case PortalIdEnum.Aek:
				return false;
			case PortalIdEnum.Paphos:
				return false;
			case PortalIdEnum.Paeek:
				return false;
			case PortalIdEnum.Doxa:
				return false;
			case PortalIdEnum.Kop:
				return false;
			default:
				return false;
		}
	}
}
